import { parseWorldDraft, parseWorldSnapshot } from '../../domain/parse.js';
import type { CharacterDraftMeta, WorldDraft, WorldSnapshot } from '../../domain/types.js';
import type { Npc } from '../../types.js';
import type { CharacterBodySnapshot } from './characterBody.js';

export const DRAFT_STORAGE_KEY = 'specimen-observer:world-draft';
export const SNAPSHOT_STORAGE_KEY = 'specimen-observer:world-snapshot';
export const LAUNCH_HANDOFF_KEY = 'specimen-observer:world-launch-handoff';
export const CHARACTER_BODIES_KEY = 'specimen-observer:character-bodies';

export interface WorldLaunchHandoff {
  schemaVersion: '1.0.0';
  snapshot: WorldSnapshot;
  characters: CharacterDraftMeta[];
  roster: Npc[];
  savedAt: string;
}

export function saveDraftToStorage(draft: WorldDraft): void {
  try {
    localStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify(draft));
  } catch {
    // Unwritable storage — session-only draft.
  }
}

export function loadDraftFromStorage(): WorldDraft | null {
  try {
    const raw = localStorage.getItem(DRAFT_STORAGE_KEY);
    if (!raw) return null;
    const parsed = parseWorldDraft(JSON.parse(raw));
    return parsed.ok ? (parsed.value as WorldDraft) : null;
  } catch {
    return null;
  }
}

export function clearDraftFromStorage(): void {
  try {
    localStorage.removeItem(DRAFT_STORAGE_KEY);
  } catch {
    // ignore
  }
}

export function saveSnapshotToStorage(snapshot: WorldSnapshot): void {
  try {
    localStorage.setItem(SNAPSHOT_STORAGE_KEY, JSON.stringify(snapshot));
  } catch {
    // ignore
  }
}

export function loadSnapshotFromStorage(): WorldSnapshot | null {
  try {
    const raw = localStorage.getItem(SNAPSHOT_STORAGE_KEY);
    if (!raw) return null;
    const parsed = parseWorldSnapshot(JSON.parse(raw));
    return parsed.ok ? (parsed.value as WorldSnapshot) : null;
  } catch {
    return null;
  }
}

export function saveLaunchHandoff(handoff: WorldLaunchHandoff): void {
  try {
    localStorage.setItem(LAUNCH_HANDOFF_KEY, JSON.stringify(handoff));
    saveSnapshotToStorage(handoff.snapshot);
  } catch {
    // ignore
  }
}

export function loadLaunchHandoff(): WorldLaunchHandoff | null {
  try {
    const raw = localStorage.getItem(LAUNCH_HANDOFF_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw) as WorldLaunchHandoff;
    if (!data?.snapshot || !Array.isArray(data.roster)) return null;
    return data;
  } catch {
    return null;
  }
}

export function clearLaunchHandoff(): void {
  try {
    localStorage.removeItem(LAUNCH_HANDOFF_KEY);
  } catch {
    // ignore
  }
}

export function saveCharacterBodies(
  draftId: string,
  bodies: Record<string, CharacterBodySnapshot>,
): void {
  try {
    const all = loadAllCharacterBodies();
    all[draftId] = bodies;
    localStorage.setItem(CHARACTER_BODIES_KEY, JSON.stringify(all));
  } catch {
    // ignore
  }
}

export function loadCharacterBodies(draftId: string): Record<string, CharacterBodySnapshot> {
  try {
    return loadAllCharacterBodies()[draftId] ?? {};
  } catch {
    return {};
  }
}

function loadAllCharacterBodies(): Record<string, Record<string, CharacterBodySnapshot>> {
  try {
    const raw = localStorage.getItem(CHARACTER_BODIES_KEY);
    return raw ? JSON.parse(raw) as Record<string, Record<string, CharacterBodySnapshot>> : {};
  } catch {
    return {};
  }
}

export function clearCharacterBodies(draftId: string): void {
  try {
    const all = loadAllCharacterBodies();
    delete all[draftId];
    localStorage.setItem(CHARACTER_BODIES_KEY, JSON.stringify(all));
  } catch {
    // ignore
  }
}
